import React, { useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

/* Enough colours to give every bar its own without repeating on a report
   with a dozen groups; beyond that it wraps, which is fine — by then the
   labels are carrying the meaning, not the colour. */
const PALETTE = [
  "#4f46e5", "#1b7f4d", "#c25e00", "#b3306f", "#0f6f68",
  "#1565c0", "#5e35b1", "#b71c1c", "#e65100", "#00695c",
];

/**
 * The summary chart that sits above the summary table.
 *
 * Bars, horizontal, sorted biggest first — the question a summary answers is
 * "who has the most", and a horizontal bar is the one chart type that can
 * carry a long label like "SRI CHANNABASAVESHWAR FIBER" without turning it
 * on its side.
 *
 * Props:
 *   rows      : [{ label, count, totals: {key: n} }]
 *   metricKey : which number to plot — "count" or a totals key
 *   metricLabel
 */
const ReportSummaryChart = ({ rows = [], metricKey = "count", metricLabel = "Tasks" }) => {
  const data = useMemo(() => {
    const valueOf = (r) =>
      metricKey === "count" ? r.count : Number(r.totals?.[metricKey] ?? 0);

    /* Top 12 only. A hundred bars is a grey smear, and the table underneath
       has every row anyway — the chart is for the shape, not the detail. */
    const top = [...rows]
      .map((r) => ({ label: r.label, value: valueOf(r) }))
      .filter((r) => r.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 12);

    return {
      labels: top.map((r) => r.label),
      datasets: [
        {
          label: metricLabel,
          data: top.map((r) => r.value),
          backgroundColor: top.map((_, i) => PALETTE[i % PALETTE.length]),
          borderRadius: 3,
          barThickness: 14,
        },
      ],
      shown: top.length,
    };
  }, [rows, metricKey, metricLabel]);

  if (!data.shown) return null;

  return (
    <div style={{ height: `${Math.max(120, data.shown * 22 + 40)}px` }}>
      <Bar
        data={data}
        options={{
          indexAxis: "y",
          responsive: true,
          maintainAspectRatio: false,
          /* Off for print: a bar that is still growing when the browser
             takes its snapshot comes out half-drawn. */
          animation: false,
          plugins: {
            legend: { display: false },
            tooltip: { enabled: true },
          },
          scales: {
            x: {
              beginAtZero: true,
              ticks: { font: { size: 8 } },
              grid: { color: "#eef0f4" },
            },
            y: {
              ticks: { font: { size: 8 }, autoSkip: false },
              grid: { display: false },
            },
          },
        }}
      />
    </div>
  );
};

export default ReportSummaryChart;
